import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/browser';
import { AuthService, UserService, PermissionsService } from '@sp-core-services';
import { CValidators } from '@sp-helpers/validation/validators';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { InfoPopupComponent } from '@sp-shared/containers/info-popup/containers/info-popup/info-popup.component';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of, Subject, throwError } from 'rxjs';
import { TwoFactorLoginComponent } from '@sp-shared/containers/two-factor-login/containers/two-factor-login/two-factor-login.component';
import { WindowService } from '@sp-core/services/window/window.service';
import { ServerErrorModel } from '@sp-core/models/app-models/server-error.model';
import { ServerStatusCode } from '@sp-core/agreement-keys/server-status-code.enum';
import { IInfoPopupData } from '@sp-shared/containers/info-popup/interfaces/info-popup-data-interface';
import { Roles } from '@sp-core/agreement-keys/roles.enum';
import { ISignInForm } from '@sp-core/models/forms-models/sign-in-form.interface';

@Component({
  selector: 'sp-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class SignInComponent implements OnInit, OnDestroy {
  public destroy$: Subject<void> = new Subject<void>();
  public signInForm: FormGroup<ISignInForm>;
  public serverError: string;
  public isSend = false;
  public shouldChooseRole = false;
  public user: IUser;
  public role: IRole;
  public userRoles: typeof Roles = Roles;

  public userRolesImages: { [key: string]: string } = {
    [Roles.masterAdmin]: 'practice-admin-role.png',
    [Roles.provider]: 'provider-role.png',
    [Roles.supportStaff]: 'support-staff-role.png',
    [Roles.practiceAdmin]: 'practice-admin-role.png',
    [Roles.dosingProvider]: 'provider-role.png',
    [Roles.limitedProvider]: 'provider-role.png',
    [Roles.limitedPracticeAdmin]: 'practice-admin-role.png',
    [Roles.limitedSupportStaff]: 'support-staff-role.png',
    [Roles.starterProvider]: 'starter-provider-role.png',
  };

  // Bound to the role that failed, not to the screen: with several roles the list is thousands
  // of pixels tall, and a message rendered at the top of the page is invisible to someone who
  // just clicked a tile near the bottom - the failure would look like nothing happening at all.
  public roleSwitchError: { roleId: number; message: string } = null;

  // Separate from isSend on purpose: a single-role user reaches updateRole automatically from
  // listenLogoutEvent while the login request is still in flight, so guarding on isSend would
  // lock those users out of the application entirely.
  private isRoleSwitching = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private cd: ChangeDetectorRef,
    public dialog: MatDialog,
    public translate: TranslateService,
    public window: WindowService,
    private userService: UserService,
    private permissionsService: PermissionsService,
  ) {
    this.checkRedirectionAndClearAuthData();
  }

  public ngOnInit(): void {
    this.authService.saveRoutePath();
    this.createSignInForm();
    this.listenLogoutEvent();
  }

  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  // The role tiles are the only way into the application, so a failed switch must not read as a
  // dead click. isSend renders the existing overlay, which shows progress and swallows repeat
  // clicks - the endpoint registers a device on the backend, so a double click is not free.
  public updateRole(role: IRole): void {
    if (this.isRoleSwitching) {
      return;
    }

    this.isRoleSwitching = true;
    this.isSend = true;
    this.serverError = null;
    this.roleSwitchError = null;

    this.permissionsService
      .updateRole(role)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isRoleSwitching = false;
          this.isSend = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        error: (error: ServerErrorModel) => {
          this.roleSwitchError = { roleId: role?.id, message: this.resolveRoleSwitchError(error) };
        },
      });
  }

  // The role screen has no other way back to the login form: a reload restores the same session
  // from the token cookie and fails the same way. A fresh login gets a new device from the backend.
  // No logout request: it needs a role the user does not have, and the next login ends the old
  // session anyway. The error is reset so it does not reappear above the login form.
  // The button sits inside the role tile, so the click must not also pick the role again.
  public signInAgain(event: Event): void {
    event.stopPropagation();
    Sentry.addBreadcrumb({ category: 'auth', message: 'Sign in again after failed role switch' });
    this.roleSwitchError = null;
    this.authService.clearData();
  }

  public onSubmit(): void {
    if (this.isSend) {
      return;
    }
    this.isSend = true;

    this.authService
      .checkExpiredPassword(this.signInForm.getRawValue())
      .pipe(
        switchMap((data) => {
          if (data.checkExpiredPassword) {
            this.router.navigateByUrl(
              `/change-password?email=${this.window.encodeURIComponent(this.signInForm.value.email)}`,
            );
            return of(null);
          }
          return this.authService.checkDeviceLogin(this.signInForm.getRawValue());
        }),
      )
      .pipe(
        switchMap((check) => this.handlerDeviceConfirmation(check)),
        switchMap((answer) => {
          if (!answer) {
            return of(null);
          }
          return this.getTypeAuth().pipe(switchMap((data) => this.handlerTwoFactorAuth(data)));
        }),
        finalize(() => {
          this.isSend = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe(
        (user: IUser) => {
          if (!user) {
            this.serverError = null;
            this.cd.markForCheck();
            this.userService.user = user;
          }
        },
        (error: ServerErrorModel) => {
          if (error.status === ServerStatusCode.customError) {
            this.serverError = error.message;
          }
        },
      );
  }

  // Device-registration gate (single active session per account). One confirmation popup,
  // triggered only when the BE reports a device switch (requiresConfirmation). Cancelling
  // aborts the login: the FE simply never calls signIn, which is how consent is enforced.
  private handlerDeviceConfirmation(check: IDeviceLoginCheck): Observable<boolean> {
    // No check result -> the expired-password branch already navigated away; abort the pipeline.
    if (!check) {
      return of(false);
    }
    // First device (empty list) or same last-used device -> register/login silently, no popup.
    if (!check.requiresConfirmation) {
      return of(true);
    }

    const data: IInfoPopupData = {
      title: 'AUTH.DEVICE_SWITCH_TITLE',
      // Copy varies by whether this is a brand-new device or a switch to another registered one.
      text: check.isNewDevice ? 'AUTH.DEVICE_SWITCH_NEW_TEXT' : 'AUTH.DEVICE_SWITCH_EXISTING_TEXT',
      actions: {
        accept: 'AUTH.DEVICE_SWITCH_CONFIRM',
        reject: 'AUTH.DEVICE_SWITCH_CANCEL',
      },
    };

    return this.dialog
      .open(InfoPopupComponent, {
        data,
        disableClose: true,
        maxHeight: '90vh',
        panelClass: 'ip-warning-modal',
      })
      .afterClosed();
  }

  // 409 is this API's "custom error": the body carries a message written for the user and the
  // interceptors deliberately leave it alone, so every component is expected to show it as-is -
  // onSubmit above does exactly that. Falling back to our own wording would throw away the only
  // explanation the backend gives for refusing the role.
  private resolveRoleSwitchError(error: ServerErrorModel): string {
    if (error?.status === ServerStatusCode.customError && error?.message) {
      return error.message;
    }

    return this.translate.instant('AUTH.ROLE_SWITCH_FAILED');
  }

  private createSignInForm(): void {
    this.signInForm = new FormGroup<ISignInForm>({
      email: new FormControl<null | string>(null, [
        CValidators.required,
        CValidators.maxLength(250),
        CValidators.noStartAndEndWhitespace,
      ]),
      password: new FormControl<null | string>(null, [CValidators.required, CValidators.maxLength(30)]),
    });
  }

  public handlerTwoFactorAuth(data: ITwoFactorAuthorization): Observable<any> {
    if (!data) {
      return of(null);
    }
    const hash = this.authService.getRememberMeHash(this.signInForm.value.email);
    if (hash && hash !== 'null') {
      return this.authService
        .signIn({
          ...this.signInForm.getRawValue(),
          remember_me_hash: hash,
        })
        .pipe(
          catchError((error: ServerErrorModel) => {
            if (error.status === ServerStatusCode.customError) {
              this.authService.cleanRememberMeHash(this.signInForm.value.email);
              if (data.is2faAvailable) {
                return this.openTwoFactorAuthPopup();
              }
            }
            return throwError(error);
          }),
        );
    }

    if (data.is2faAvailable) {
      return this.openTwoFactorAuthPopup();
    }
    return this.authService.signIn({
      ...this.signInForm.getRawValue(),
    });
  }

  public openTwoFactorAuthPopup(): Observable<any> {
    return this.dialog
      .open(TwoFactorLoginComponent, {
        data: {
          login: this.signInForm.value,
        },
        disableClose: true,
      })
      .afterClosed();
  }

  public getTypeAuth(): Observable<ITwoFactorAuthorization> {
    return this.authService.getUserType2FANonLogin(this.signInForm.getRawValue());
  }

  // clear auth data if user redirect to page after init application
  private checkRedirectionAndClearAuthData() {
    if (this.router.navigated) {
      this.userService.user = null;
      this.permissionsService.role = null;
    }
  }

  // needs for reset page states when user logout on current page
  private listenLogoutEvent() {
    combineLatest([this.userService.user$, this.permissionsService.role$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user, role]) => {
        if (!user) {
          this.isSend = false;
          this.user = null;
          this.role = null;
          this.shouldChooseRole = false;
          this.serverError = null;
          if (this.signInForm) {
            this.signInForm.reset();
          }
          this.cd.detectChanges();
          return;
        }

        if (user && role) {
          this.authService.handlerNavigation(role);
          return;
        }

        if (user && !user.isMultipleRoles) {
          this.updateRole(user.roles[0]);
          return;
        }

        if (user && user.isMultipleRoles) {
          this.user = user;
          this.shouldChooseRole = true;
          this.serverError = null;
          this.cd.detectChanges();
        }
      });
  }
}
